import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Zap } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR } from '../../lib/format';

export default function InternalHireTab() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await apiGet(`/equipment/internal-hire-charges?month=${month}`)); }
    catch { setData({ items: [], total_charge: 0 }); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await apiPost('/equipment/internal-hire-charges/generate', { month });
      toast.success(`${r.generated} charge(s) generated · ${formatINR(r.total_charge)}`);
      if (r.skipped?.length) toast.info(`${r.skipped.length} machine(s) skipped (no hire rate set)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  if (data === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="internal-hire-tab">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="kpi-overline flex items-center gap-1.5"><Zap size={11} /> Internal Hire Charges</div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="ih-month-input" />
        <span className="text-xs text-slate-500">Own machines charged to the projects using them — flows into the project committed-cost view.</span>
        <button onClick={generate} disabled={busy} data-testid="ih-generate-btn"
          className="ml-auto px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50">
          {busy ? 'Generating…' : `Generate Charges for ${month}`}
        </button>
      </div>

      {data.items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">
          No charges for {month} — set hire rates on machines, then generate
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="ih-charges-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Machine</th>
                <th className="px-4 py-2.5 text-left">Charged To Project</th>
                <th className="px-4 py-2.5 text-left">Month</th>
                <th className="px-4 py-2.5 text-right">Hours Used</th>
                <th className="px-4 py-2.5 text-right">Rate (₹/hr)</th>
                <th className="px-4 py-2.5 text-right">Charge</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(r => (
                <tr key={r.id} data-testid={`ih-row-${r.eq_id}`} className="border-b border-slate-100 hover:bg-slate-50/40">
                  <td className="px-4 py-2.5 font-mono text-xs">{r.equipment_name}</td>
                  <td className="px-4 py-2.5 text-xs">{r.project_name || r.project_id || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.month}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs">{r.hours_used}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs">{Number(r.rate).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs font-display font-bold">{formatINR(r.charge_amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td colSpan="5" className="px-4 py-2.5 text-right text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Total Internal Hire</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-display font-extrabold" data-testid="ih-total">{formatINR(data.total_charge)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
